import type { IndustryContent } from '@/types/content';

export const handymanContent: IndustryContent = {
  slug: 'handyman',
  name: 'Handyman',
  headline: 'The Operating System for Handyman Businesses',
  subheadline:
    'Manage diverse jobs, track multiple skills, and build repeat customers—all from one platform built for handyman pros.',
  heroImageUrl: '/images/industries/handyman-hero.webp',
  painPoints: [
    {
      title: 'Every job is different',
      description:
        'Drywall repair at 9am, toilet fix at 11am, ceiling fan install at 2pm. The variety makes scheduling and pricing complex.',
      icon: 'variety',
    },
    {
      title: 'Pricing is all over the place',
      description:
        'Hourly? Flat rate? Per-task? Without consistent pricing, you leave money on the table or scare customers away.',
      icon: 'pricing',
    },
    {
      title: 'Parts runs kill productivity',
      description:
        'Arriving at a job and realizing you need a trip to the hardware store wastes time and frustrates customers.',
      icon: 'parts',
    },
    {
      title: 'Repeat customers forget about you',
      description:
        'Customers loved your work but forgot your name six months later. Staying top-of-mind is hard.',
      icon: 'retention',
    },
    {
      title: 'Jack-of-all-trades perception',
      description:
        'Customers wonder if you can really handle their specific problem. Building trust with new customers takes time.',
      icon: 'trust',
    },
  ],
  features: [
    {
      title: 'Multi-Skill Dispatching',
      description:
        'Match jobs to technicians based on skills. Electrical to the licensed guy, plumbing to the certified pro.',
      icon: 'dispatch',
    },
    {
      title: 'Task-Based Pricing',
      description:
        'Build a price book of common tasks. Quote consistently and track profitability by service type.',
      icon: 'pricing',
    },
    {
      title: 'Pre-Job Checklists',
      description:
        'Know what parts you need before you leave. Reduce parts runs and show up prepared.',
      icon: 'checklist',
    },
    {
      title: 'Customer Retention Automation',
      description:
        'Automated follow-ups, seasonal reminders, and "time for maintenance" nudges keep you top-of-mind.',
      icon: 'retention',
    },
    {
      title: 'Service History',
      description:
        'Complete history of every job at every property. Return visits start with full context.',
      icon: 'history',
    },
    {
      title: 'Review Generation',
      description:
        'Automated review requests after completed jobs. Build your reputation with happy customer feedback.',
      icon: 'reviews',
    },
  ],
  testimonial: {
    id: 'handyman-1',
    quote:
      'The automated follow-ups bring in 2-3 repeat jobs per week that I would have lost. Those customers thought I retired!',
    author: 'Tom Bradley',
    role: 'Owner',
    company: 'Bradley Home Services',
    industry: 'handyman',
    stats: {
      label: 'Repeat Business Increase',
      value: '+40%',
    },
  },
  stats: [
    { label: 'Repeat Customers', value: '+40%' },
    { label: 'Parts Runs Reduced', value: '-60%' },
    { label: 'Pricing Consistency', value: '95%' },
    { label: 'Customer Reviews', value: '4.9★' },
  ],
  seo: {
    title: 'Handyman Software | Scheduling & Job Management | Omniforge',
    description:
      'Handyman business software for home repair professionals. Multi-skill dispatching, task pricing, and customer retention tools. Start your free trial.',
    keywords: [
      'handyman software',
      'handyman business software',
      'handyman scheduling software',
      'home repair software',
      'handyman app',
    ],
  },
};
